import * as Blockly from 'blockly/core';
import { useBlockModalSpeakerStore } from '@/stores/useBlockModalSpeakerStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { darken } from '@/(routers)/boards/editor/block/blockTheme';

export interface NoteData {
    type: 'note' | 'rest';
    pitch?: string;
    octave?: number;
    duration?: number;
}

const FIELD_HEIGHT = 42;
const FIELD_MIN_WIDTH = 90;
const SWATCH_SIZE = 24;
const SWATCH_RADIUS = 4;
const SWATCH_GAP = 6;
const PADDING = 8;
const SWATCH_FONT_SIZE = 11;

const PITCH_COLORS: Record<string, string> = {
    C: '#fc1b18',
    D: '#fb8c00',
    E: '#fdd835',
    F: '#2c9631',
    G: '#00cfeb',
    A: '#004ddd',
    B: '#8e24aa',
};

const PITCH_TO_JP: Record<string, string> = {
    C: 'ド',
    D: 'レ',
    E: 'ミ',
    F: 'ファ',
    G: 'ソ',
    A: 'ラ',
    B: 'シ',
};

export class FieldPianoRoll extends Blockly.Field<string> {
    private notes_: NoteData[];
    private labelEl_: SVGTextElement | null = null;
    private swatchEls_: SVGElement[] = [];

    static override fromJson(options: Blockly.FieldConfig & { notes?: NoteData[] }): FieldPianoRoll {
        return new FieldPianoRoll(options.notes ?? []);
    }

    constructor(notes: NoteData[]) {
        super(JSON.stringify(notes));
        this.notes_ = notes;
        this.SERIALIZABLE = true;
    }

    override initView(): void {
        const width = this.calcWidth_();

        this.borderRect_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 6, ry: 6, x: 0, y: 0, width, height: FIELD_HEIGHT },
            this.fieldGroup_,
        );

        this.labelEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.TEXT,
            {
                x: PADDING,
                y: FIELD_HEIGHT / 2 + 6,
                'font-size': '17px',
                'font-weight': '500',
                'font-family': 'Corporate Logo, sans-serif',
                style: 'fill: white !important;',
            },
            this.fieldGroup_,
        ) as SVGTextElement;

        this.updateDisplay_();

        requestAnimationFrame(() => {
            this.applyStyle_();
            this.updateDisplay_();
            (this.getSourceBlock() as Blockly.BlockSvg)?.render();
            this.fieldGroup_?.addEventListener('mouseenter', () => this.applyStyle_(true));
            this.fieldGroup_?.addEventListener('mouseleave', () => this.applyStyle_(false));
        });
    }

    private applyStyle_(hover = false): void {
        if (!this.borderRect_) return;
        const colour = this.getSourceBlock()?.getColour() ?? '#e65100';
        const fillColour = this.notes_.length === 0 ? darken(colour) : colour;
        this.borderRect_.setAttribute(
            'style',
            `fill: ${fillColour} !important; stroke: ${
                hover ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.3)'
            }; stroke-width: ${hover ? 2 : 1};`,
        );
    }

    private calcWidth_(): number {
        if (this.notes_.length === 0) return FIELD_MIN_WIDTH;
        return PADDING + this.notes_.length * (SWATCH_SIZE + SWATCH_GAP) - SWATCH_GAP + PADDING;
    }

    private updateDisplay_(): void {
        if (!this.fieldGroup_ || !this.labelEl_) return;

        this.swatchEls_.forEach((el) => el.parentNode?.removeChild(el));
        this.swatchEls_ = [];

        if (this.notes_.length === 0) {
            this.labelEl_.setAttribute('visibility', 'visible');
            this.labelEl_.textContent = '設定した音';
        } else {
            this.labelEl_.setAttribute('visibility', 'hidden');

            const swatchY = (FIELD_HEIGHT - SWATCH_SIZE) / 2;
            this.notes_.forEach((note, i) => {
                const x = PADDING + i * (SWATCH_SIZE + SWATCH_GAP);
                const isRest = note.type === 'rest';
                const color = isRest ? 'rgba(0,0,0,0.3)' : PITCH_COLORS[note.pitch ?? ''] ?? '#888';
                const label = isRest ? '休' : PITCH_TO_JP[note.pitch ?? ''] ?? note.pitch ?? '?';

                const rect = Blockly.utils.dom.createSvgElement(
                    Blockly.utils.Svg.RECT,
                    {
                        x,
                        y: swatchY,
                        width: SWATCH_SIZE,
                        height: SWATCH_SIZE,
                        rx: SWATCH_RADIUS,
                        ry: SWATCH_RADIUS,
                        style: `fill: ${color} !important; stroke: rgba(0,0,0,0.3); stroke-width: 1;`,
                    },
                    this.fieldGroup_,
                );

                const text = Blockly.utils.dom.createSvgElement(
                    Blockly.utils.Svg.TEXT,
                    {
                        x: x + SWATCH_SIZE / 2,
                        y: swatchY + SWATCH_SIZE / 2 + SWATCH_FONT_SIZE / 2 - 1,
                        'font-size': `${SWATCH_FONT_SIZE}px`,
                        'font-weight': '600',
                        'text-anchor': 'middle',
                        style: isRest
                            ? 'fill: rgba(255,255,255,0.5) !important;'
                            : 'fill: rgba(255,255,255,0.9) !important;',
                    },
                    this.fieldGroup_,
                ) as SVGTextElement;
                text.textContent = label;

                this.swatchEls_.push(rect, text);
            });
        }

        const width = this.calcWidth_();
        this.borderRect_?.setAttribute('width', String(width));
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
        this.applyStyle_();
        (this.getSourceBlock() as Blockly.BlockSvg)?.render();
    }

    public setNotes(notes: NoteData[]): void {
        this.notes_ = notes;
        const json = JSON.stringify(notes);
        this.setValue(json);
        const block = this.getSourceBlock();
        if (block) {
            Blockly.Events.fire(new Blockly.Events.BlockChange(block, 'field', this.name ?? '', '', json));
        }
    }

    public getNotes(): NoteData[] {
        return this.notes_;
    }

    override doValueUpdate_(newValue: string): void {
        try {
            this.notes_ = JSON.parse(newValue) as NoteData[];
        } catch {
            this.notes_ = [];
        }
        if (this.fieldGroup_) {
            this.updateDisplay_();
        }
    }

    override getValue(): string {
        return JSON.stringify(this.notes_);
    }

    override getSize(): Blockly.utils.Size {
        const width = this.calcWidth_();
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
        return this.size_;
    }

    override showEditor_(): void {
        const { setFieldRef } = useBlockModalSpeakerStore.getState();
        const { openModal } = useBlockModalStore.getState();
        setFieldRef({ setNotes: (notes) => this.setNotes(notes), getNotes: () => this.getNotes() });
        openModal('speaker');
    }
}
